import React from 'react';
import './paperwork.css';
import { COMPANY } from '../../constants/company';

const Header = () => (
  <div className="flex items-start justify-between">
    <div className="flex items-center gap-2">
      <svg viewBox="0 0 40 40" className="w-10 h-10">
        <circle cx="20" cy="20" r="20" fill="#1F0068" />
        <path d="M12 22 L18 28 L28 14" fill="none" stroke="#FF5C39" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="leading-tight">
        <div className="font-bold text-[15px] text-[#1F0068]">{COMPANY.shortName}</div>
        <div className="text-[9px] tracking-[0.22em] text-[#FF5C39] uppercase font-semibold">{COMPANY.tagline}</div>
      </div>
    </div>
    <div className="text-right text-[10px] text-[#1F0068]/70 leading-relaxed">
      <div>{COMPANY.phone}</div>
      <div>{COMPANY.email}</div>
      <div>{COMPANY.registeredOffice.full}</div>
    </div>
  </div>
);

const Invoice = ({ policy }) => {
  const fullName = `${policy.title} ${policy.firstName} ${policy.lastName}`;
  const amount = Number(policy.amount);
  const partials = Array.isArray(policy.partialPayments) ? policy.partialPayments : [];
  const paidToDate = partials.reduce((s, pp) => s + Number(pp.amount || 0), 0);
  // If the plan is fully paid, treat the whole premium as received (covers the case
  // where the balance was settled outside the partial-payments workflow).
  const isFullyPaid = policy.paymentStatus === 'Paid';
  const effectivePaid = isFullyPaid ? amount : Math.min(amount, paidToDate);
  const outstanding = Math.max(0, +(amount - effectivePaid).toFixed(2));
  const hasPartials = partials.length > 0 || isFullyPaid;
  const invoiceNo = `DHS/INV/${policy.planNumber.replace('DHS', '')}`;

  return (
    <div className="a4-doc">
      <div className="a4-brand-strip" />
      <Header />

      <div className="mt-6 grid grid-cols-[1fr_auto] gap-4 items-end">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF5C39] font-bold">Invoice</div>
          <div className="mt-1 flex items-baseline gap-4 text-[11px] text-[#1F0068]/80">
            <div><span className="font-bold text-[#1F0068]">Invoice No:</span> {invoiceNo}</div>
            <div><span className="font-bold text-[#1F0068]">Invoice Date:</span> {new Date(policy.createdAt || policy.policyStart).toLocaleDateString('en-GB')}</div>
            <div><span className="font-bold text-[#1F0068]">Payment Due:</span> {new Date(policy.paymentDue).toLocaleDateString('en-GB')}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">{hasPartials ? 'Balance Due' : 'Amount Due'}</div>
          <div className="text-3xl font-extrabold text-[#FF5C39]">£ {outstanding.toFixed(2)}</div>
          {hasPartials && effectivePaid > 0 && (
            <div data-testid="invoice-paid-so-far" className="text-[10px] text-[#1F0068]/60 mt-0.5">Paid so far: £{effectivePaid.toFixed(2)} of £{amount.toFixed(2)}</div>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="bg-[#FFF6EB] rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Billed To</div>
          <div className="mt-2 text-[11px] text-[#1F0068]/85 space-y-1">
            <div><span className="text-[#1F0068]/60">Customer Name:</span> <span className="font-bold text-[#1F0068]">{fullName}</span></div>
            <div><span className="text-[#1F0068]/60">Address:</span> {policy.address1}{policy.address2 ? ', ' + policy.address2 : ''}</div>
            <div><span className="text-[#1F0068]/60">City:</span> {policy.city}</div>
            <div><span className="text-[#1F0068]/60">Postcode:</span> {policy.postcode}</div>
          </div>
        </div>
        <div className="bg-[#FFEDD6] rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Plan Summary</div>
          <div className="mt-2 text-[11px] text-[#1F0068]/85 space-y-1">
            <div><span className="text-[#1F0068]/60">Plan Number:</span> <span className="font-bold text-[#1F0068]">{policy.planNumber}</span></div>
            <div><span className="text-[#1F0068]/60">Plan Type:</span> Maintenance Cover Plan</div>
            <div><span className="text-[#1F0068]/60">Payment Method:</span> Direct Bank Transfer</div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Description</div>
        <div className="mt-2 bg-white border border-[#1F0068]/10 rounded-xl p-3">
          <div className="font-bold text-[#1F0068] text-[12px]">Products Covered</div>
          <div className="text-[11px] text-[#1F0068]/85 mt-1">{policy.appliances.join(', ')}</div>
        </div>
      </div>

      {partials.length > 0 && (
        <div data-testid="invoice-payment-history" className="mt-4 bg-[#E8DDFF] rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Payment history</div>
          <div className="mt-2 space-y-1 text-[11px] text-[#1F0068]/85">
            {partials.map((pp, i) => (
              <div key={pp.at + '-' + i} className="flex items-center justify-between border-b border-[#1F0068]/5 last:border-0 py-1">
                <div>
                  <b className="text-[#1F0068]">£ {Number(pp.amount || 0).toFixed(2)}</b>
                  <span className="text-[#1F0068]/60"> · {new Date(pp.at).toLocaleDateString('en-GB')}</span>
                  {pp.note ? <span className="text-[#1F0068]/60"> · {pp.note}</span> : null}
                </div>
                <span className="text-[10px] uppercase font-bold text-[#00b67a]">Received</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 bg-[#1F0068] text-white rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#FF5C39] font-bold">Direct Bank Transfer Details</div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-[12px]">
          <div><span className="text-white/70 text-[10px] uppercase tracking-widest font-bold block">Account Name</span><span className="font-bold">{COMPANY.bank.accountName}</span></div>
          <div><span className="text-white/70 text-[10px] uppercase tracking-widest font-bold block">Sort Code</span><span className="font-bold">{COMPANY.bank.sortCode}</span></div>
          <div><span className="text-white/70 text-[10px] uppercase tracking-widest font-bold block">Account Number</span><span className="font-bold">{COMPANY.bank.accountNumber}</span></div>
          <div><span className="text-white/70 text-[10px] uppercase tracking-widest font-bold block">Payment Reference</span><span className="font-bold">{policy.planNumber}</span></div>
        </div>
        <p className="mt-3 text-[10px] text-white/70 italic">Please ensure the correct reference is used so your payment can be matched promptly.</p>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-[11px]">
        <div className="col-span-2 bg-[#FFF6EB] rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Important Information</div>
          <p className="mt-1 text-[10px] text-[#1F0068]/80 leading-relaxed">Once your payment has been received and cleared, your Home Cover Plan will be activated. Please retain this invoice for your records. Your plan is subject to the applicable Terms & Conditions.</p>
        </div>
        <div className="bg-white border border-[#1F0068]/10 rounded-xl p-3 text-right text-[11px]">
          <div className="flex justify-between"><span className="text-[#1F0068]/60">Original Amount</span><span className="font-semibold">£ {amount.toFixed(2)}</span></div>
          {hasPartials && (
            <div data-testid="invoice-amount-paid-row" className="flex justify-between mt-1"><span className="text-[#1F0068]/60">Amount Paid</span><span className="font-semibold text-[#00b67a]">− £ {effectivePaid.toFixed(2)}</span></div>
          )}
          <div className="flex justify-between mt-2 pt-2 border-t border-[#1F0068]/10"><span className="font-bold text-[#1F0068]">{hasPartials ? 'Remaining Amount Due' : 'Total Due'}</span><span data-testid="invoice-remaining-due" className="font-extrabold text-[#FF5C39] text-lg">£ {outstanding.toFixed(2)}</span></div>
        </div>
      </div>

      <div className="mt-4 bg-white border border-[#1F0068]/10 rounded-xl p-3">
        <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Need Help?</div>
        <div className="mt-1 text-[11px] text-[#1F0068]/85">Our UK-based customer service team is here to assist.</div>
        <div className="mt-1 text-[11px] text-[#1F0068]"><b>Telephone:</b> {COMPANY.phone} · <b>Email:</b> {COMPANY.email} · <b>Hours:</b> Monday–Friday, 9am–5pm</div>
      </div>

      <div className="a4-footer">
        <div>Invoice {invoiceNo} · Reference {policy.planNumber}</div>
        <div>{COMPANY.name} · Company No. {COMPANY.companyNumber} · {COMPANY.registeredOffice.short}</div>
      </div>
    </div>
  );
};

export default Invoice;
